import React, { useEffect, useState, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Input, Select, Alert, Tag, Typography, Row, Col, Space, theme, Upload, message, Popconfirm,
} from 'antd';
import {
  AppstoreOutlined, FolderOpenOutlined, FireOutlined, ThunderboltOutlined, ApiOutlined,
  CloudOutlined, ColumnWidthOutlined, BankOutlined, CalendarOutlined,
  SafetyCertificateOutlined, EnvironmentOutlined, EditOutlined, FilePdfOutlined,
  TeamOutlined, MessageOutlined, CameraOutlined, LoadingOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { getProject, updateProject, removeProjectSitePhoto, getOrgMembers } from '../firebase/firestore';
import { uploadProjectSitePhoto, deleteProjectSitePhoto } from '../firebase/storage';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import { useShellTitle } from '../components/AppShell';
import ErrorBoundary from '../components/ErrorBoundary';
import { Project, UserProfile } from '../models/types';
import { TOOLS, isToolEnabled, ToolId } from '../config/tools';
import EbtronModule from '../modules/ebtron/EbtronModule';
import PowerModule from '../modules/power/PowerModule';
import PowerFactorModule from '../modules/powerFactor/PowerFactorModule';
import DaltModule from '../modules/dalt/DaltModule';
import FacilityModule from '../modules/facility/FacilityModule';
import BoilerECMModule from '../modules/boilerECM/BoilerECMModule';
import PMSchedulerModule from '../modules/pmScheduler/PMSchedulerModule';
import ComplianceCalendar from '../modules/pmScheduler/ComplianceCalendar';
import DocumentLibrary from '../modules/documents/DocumentLibrary';

/* Lazy: the assistant pulls in @ant-design/x + react-markdown (~90 kB gzip).
   Only load that chunk when someone actually opens the tab. */
const BuildingAssistantModule = React.lazy(() => import('../modules/buildingAssistant/BuildingAssistantModule'));

type Tab = ToolId;

const STATUS_OPTIONS: Project['status'][] = ['active', 'completed', 'archived'];

/* AntD preset tag colors per project status */
const STATUS_TAG: Record<string, string> = {
  active: 'green', completed: 'blue', archived: 'default',
};

/* Same mapping as AppShell TOOL_ICONS */
const TOOL_ICONS: Record<ToolId, React.ReactNode> = {
  overview: <AppstoreOutlined />,
  documents: <FolderOpenOutlined />,
  boiler: <FireOutlined />,
  power: <ThunderboltOutlined />,
  powerFactor: <ApiOutlined />,
  ebtron: <CloudOutlined />,
  dalt: <ColumnWidthOutlined />,
  facility: <BankOutlined />,
  pmScheduler: <CalendarOutlined />,
  compliance: <SafetyCertificateOutlined />,
  assistant: <MessageOutlined />,
};

const ProjectHub: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization, isAdmin, orgId, isContractor, isLoading: orgLoading } = useOrg();
  const { currentUser } = useAuth();
  const { setTitle } = useShellTitle();
  const { token } = theme.useToken();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Active tab comes from the URL (?tab=) so the AppShell sidebar drives it.
  const activeTab = (searchParams.get('tab') as Tab) || 'overview';
  const [visitedTabs, setVisitedTabs] = useState<Set<Tab>>(new Set<Tab>([activeTab]));
  useEffect(() => {
    setVisitedTabs(prev => (prev.has(activeTab) ? prev : new Set<Tab>(prev).add(activeTab)));
  }, [activeTab]);

  const switchTab = (tab: Tab) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editClient, setEditClient] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editStatus, setEditStatus] = useState<Project['status']>('active');
  const [saving, setSaving] = useState(false);
  const [orgContractors, setOrgContractors] = useState<UserProfile[]>([]);
  const [savingAssign, setSavingAssign] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  /* Wait for OrgContext before the first read (proves the auth token is live — otherwise a cold PWA
     relaunch straight onto a project route reads before auth attaches and the rules reject it), then
     read cache-first: the project paints instantly from cache and refreshes in the background. A
     fresh install with nothing cached falls back to a retryable server read inside getProject. */
  /* `slow` gates the spinner: the cache-first read resolves in milliseconds on a revisit, and
     flashing a loading card for one frame then re-rendering was a visible stutter on every
     navigation. Nothing is drawn until the data lands or 300 ms pass. */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!id || orgLoading) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 300);
    getProject(id, p => { if (!cancelled && p) setProject(p); })
      .then(p => { if (cancelled) return; if (!p) setError('Project not found'); else setProject(p); })
      .catch(() => { if (!cancelled) setError('Failed to load project'); })
      .finally(() => { clearTimeout(slowTimer); if (!cancelled) { setLoading(false); setSlow(false); } });
    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [id, orgLoading]);

  // Reflect the project name in the AppShell topbar; clear it on unmount.
  useEffect(() => { setTitle(project?.name ?? ''); return () => setTitle(''); }, [project, setTitle]);

  // Admins can assign the org's contractors to this project — load the roster of contractors.
  useEffect(() => {
    if (!isAdmin || !orgId) { setOrgContractors([]); return; }
    getOrgMembers(orgId)
      .then(m => setOrgContractors(m.filter(u => u.role === 'contractor' && !u.disabled)))
      .catch(() => setOrgContractors([]));
  }, [isAdmin, orgId]);

  const saveAssignments = async (uids: string[]) => {
    if (!id || !project) return;
    setSavingAssign(true);
    try {
      /* Server-side only: the callable checks the caller is an org admin and every uid is a
         contractor of this org — the project rules refuse assignedContractors from the client. */
      await httpsCallable(functions, 'setProjectContractors')({ projectId: id, uids });
      setProject({ ...project, assignedContractors: uids });
    } catch (err: any) { setError(err?.message ?? 'Failed to update contractor assignments'); }
    finally { setSavingAssign(false); }
  };

  const handleUploadSitePhoto = async (file: File) => {
    if (!id || !project) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadProjectSitePhoto(orgId ?? null, id, file);
      await updateProject(id, { sitePhotoUrl: url });
      setProject({ ...project, sitePhotoUrl: url });
    } catch { message.error('Failed to upload site photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleRemoveSitePhoto = async () => {
    if (!id || !project) return;
    setUploadingPhoto(true);
    try {
      await removeProjectSitePhoto(id);
      await deleteProjectSitePhoto(orgId ?? null, id).catch(() => {});
      setProject({ ...project, sitePhotoUrl: undefined });
    } catch { message.error('Failed to remove site photo'); }
    finally { setUploadingPhoto(false); }
  };

  const startEdit = () => {
    if (!project) return;
    setEditName(project.name); setEditAddress(project.address ?? '');
    setEditClient(project.client ?? '');
    setEditDesc(project.description ?? ''); setEditStatus(project.status);
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!id || !project) return;
    setSaving(true);
    try {
      await updateProject(id, { name: editName, address: editAddress, client: editClient, description: editDesc, status: editStatus });
      setProject({ ...project, name: editName, address: editAddress, client: editClient, description: editDesc, status: editStatus });
      setEditMode(false);
    } catch { setError('Failed to save'); }
    finally { setSaving(false); }
  };

  // Mirrors firestore.rules' canWriteProjectData: archived locks everyone but the project's
  // owner (the "admin" for a personal, unscoped project) and org admins. 'completed' is not
  // locked. This is a UX courtesy, not the enforcement — the rules are what actually stop the
  // write; this just keeps the app from inviting an edit that would only fail server-side.
  const isLocked = !!project && project.status === 'archived' &&
    project.userId !== currentUser?.uid && !isAdmin;

  const enabledModules = organization?.enabledModules;
  // Contractors are limited to the facility assessment; everyone else sees the org's enabled set.
  const moduleVisible = (mid: string) =>
    isContractor ? mid === 'facility' : (!enabledModules || enabledModules.includes(mid));
  const visibleTools = TOOLS.filter(t =>
    isToolEnabled(t.id, enabledModules) && (!isContractor || t.id === 'overview' || t.id === 'facility'));

  const field = (label: string, node: React.ReactNode) => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  // Blank (not a spinner) while a fast cache read is in flight; spinner only once it's proven slow.
  if (loading) return slow ? <Card loading /> : null;
  if (error || !project) return (
    <Alert
      type="error" showIcon
      message={error || 'Project not found'}
      action={<Button size="small" onClick={() => navigate('/')}>Back to Dashboard</Button>}
    />
  );

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>{project.name}</Typography.Title>
            {project.address && (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                <EnvironmentOutlined /> {project.address}
              </Typography.Text>
            )}
          </div>
          <Tag color={STATUS_TAG[project.status] ?? 'default'} style={{ marginInlineEnd: 0 }}>{project.status}</Tag>
          <Button type="primary" icon={<FilePdfOutlined />} onClick={() => navigate(`/projects/${project.id}/report`)}>
            Report
          </Button>
        </div>
      </Card>

      {isLocked && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="This project is archived"
          description="Editing is restricted to the project's owner and organization admins. Ask an admin to make changes, or un-archive it first."
        />
      )}

      <div>
        {activeTab === 'overview' && (
          <div>
            {editMode ? (
              <Card title="Edit project">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                  {field('Project Name', <Input value={editName} onChange={e => setEditName(e.target.value)} />)}
                  {field('Address', <Input value={editAddress} onChange={e => setEditAddress(e.target.value)} />)}
                  {field('Client', <Input value={editClient} onChange={e => setEditClient(e.target.value)} placeholder="Who the report is prepared for" />)}
                  {field('Description', <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} />)}
                  {field('Status', (
                    <Select
                      style={{ width: '100%' }}
                      value={editStatus}
                      options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))}
                      onChange={v => setEditStatus(v)}
                    />
                  ))}
                </div>
                <Space style={{ marginTop: 16 }}>
                  <Button type="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                  <Button onClick={() => setEditMode(false)}>Cancel</Button>
                </Space>
              </Card>
            ) : (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 6 }}>{project.name}</Typography.Title>
                    {project.address && (
                      <div style={{ marginBottom: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                          <EnvironmentOutlined /> {project.address}
                        </Typography.Text>
                      </div>
                    )}
                    {project.client && (
                      <div style={{ marginBottom: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                          <TeamOutlined /> Prepared for {project.client}
                        </Typography.Text>
                      </div>
                    )}
                    {project.description && (
                      <div style={{ marginBottom: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>{project.description}</Typography.Text>
                      </div>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                      Created {new Date(project.createdAt).toLocaleDateString()} · Updated {new Date(project.updatedAt).toLocaleDateString()}
                    </Typography.Text>
                  </div>
                  {!isContractor && !isLocked && <Button icon={<EditOutlined />} onClick={startEdit}>Edit</Button>}
                </div>
              </Card>
            )}
            {isAdmin && (
              <Card size="small" style={{ marginTop: 18 }} title={<span><TeamOutlined /> Contractor access</span>}>
                <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 0 }}>
                  Contractors assigned here can open this project to fill out the facility assessment and view the report. They cannot see any other project.
                </Typography.Paragraph>
                {orgContractors.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    No contractors in your organization yet. Invite one from the Team page (role “Contractor”).
                  </Typography.Text>
                ) : (
                  <Select
                    mode="multiple" allowClear style={{ width: '100%', maxWidth: 520 }}
                    placeholder="Select contractors to grant access"
                    loading={savingAssign}
                    value={project.assignedContractors ?? []}
                    onChange={saveAssignments}
                    optionFilterProp="label"
                    options={orgContractors.map(u => ({ value: u.uid, label: u.displayName || u.email }))}
                  />
                )}
              </Card>
            )}
            {!isContractor && (
              <Card size="small" style={{ marginTop: 18 }} title={<span><CameraOutlined /> Site photo</span>}>
                <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 0 }}>
                  A labeled site or aerial photo identifying which building this project covers — shown on the detailed report's cover page.
                </Typography.Paragraph>
                {project.sitePhotoUrl && (
                  <img
                    src={project.sitePhotoUrl} alt="Project site"
                    style={{ maxWidth: '100%', maxHeight: 260, display: 'block', marginBottom: 12, borderRadius: 4 }}
                  />
                )}
                <Space>
                  <Upload
                    accept="image/*" showUploadList={false}
                    disabled={uploadingPhoto}
                    beforeUpload={file => { handleUploadSitePhoto(file); return false; }}
                  >
                    <Button icon={uploadingPhoto ? <LoadingOutlined /> : <CameraOutlined />} disabled={uploadingPhoto}>
                      {project.sitePhotoUrl ? 'Replace photo' : 'Upload photo'}
                    </Button>
                  </Upload>
                  {project.sitePhotoUrl && (
                    <Popconfirm title="Remove this site photo?" onConfirm={handleRemoveSitePhoto}>
                      <Button icon={<DeleteOutlined />} disabled={uploadingPhoto}>Remove</Button>
                    </Popconfirm>
                  )}
                </Space>
              </Card>
            )}
            <Row gutter={[16, 16]} style={{ marginTop: 18 }}>
              {visibleTools.filter(t => t.id !== 'overview').map(t => (
                <Col xs={24} sm={12} lg={8} xxl={6} key={t.id}>
                  <Card hoverable onClick={() => switchTab(t.id)} styles={{ body: { textAlign: 'center', padding: '24px 18px' } }}>
                    <div style={{ fontSize: 26, color: token.colorPrimary, marginBottom: 10 }}>{TOOL_ICONS[t.id]}</div>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>{t.label}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>Click to open</Typography.Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
        {/* Lazy-mount: only render a module after its tab has been visited. Keep mounted to avoid Firestore refetch. Wrap each in ErrorBoundary to prevent blank screen on bad data. */}
        {moduleVisible('facility') && visitedTabs.has('facility') && <ErrorBoundary><div style={{ display: activeTab === 'facility' ? 'block' : 'none' }}><FacilityModule projectId={project.id} locked={isLocked} /></div></ErrorBoundary>}
        {moduleVisible('dalt') && visitedTabs.has('dalt') && <ErrorBoundary><div style={{ display: activeTab === 'dalt' ? 'block' : 'none' }}><DaltModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('ebtron') && visitedTabs.has('ebtron') && <ErrorBoundary><div style={{ display: activeTab === 'ebtron' ? 'block' : 'none' }}><EbtronModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('power') && visitedTabs.has('power') && <ErrorBoundary><div style={{ display: activeTab === 'power' ? 'block' : 'none' }}><PowerModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('powerFactor') && visitedTabs.has('powerFactor') && <ErrorBoundary><div style={{ display: activeTab === 'powerFactor' ? 'block' : 'none' }}><PowerFactorModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('boiler') && visitedTabs.has('boiler') && <ErrorBoundary><div style={{ display: activeTab === 'boiler' ? 'block' : 'none' }}><BoilerECMModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('pmScheduler') && visitedTabs.has('pmScheduler') && <ErrorBoundary><div style={{ display: activeTab === 'pmScheduler' ? 'block' : 'none' }}><PMSchedulerModule projectId={project.id} /></div></ErrorBoundary>}
        {moduleVisible('compliance') && visitedTabs.has('compliance') && <ErrorBoundary><div style={{ display: activeTab === 'compliance' ? 'block' : 'none' }}><ComplianceCalendar projectId={project.id} projectName={project.name} orgName={organization?.name} /></div></ErrorBoundary>}
        {moduleVisible('documents') && visitedTabs.has('documents') && <ErrorBoundary><div style={{ display: activeTab === 'documents' ? 'block' : 'none' }}><DocumentLibrary projectId={project.id} projectName={project.name} /></div></ErrorBoundary>}
        {moduleVisible('assistant') && visitedTabs.has('assistant') && <ErrorBoundary><div style={{ display: activeTab === 'assistant' ? 'block' : 'none' }}><Suspense fallback={<Card loading />}><BuildingAssistantModule projectId={project.id} projectName={project.name} /></Suspense></div></ErrorBoundary>}
      </div>
    </div>
  );
};

export default ProjectHub;
